/**
 * A person as the page shows them: a short name and initials, from the email the service stores
 * (redesign, 2026-09-25). A column of `v.khomenko@mornhouse.com` wrapped onto two lines was most of
 * what a card said; `V. Khomenko` with an avatar says the same thing and leaves room for the card.
 *
 * The email stays the identity — it is the tooltip, and nothing is ever matched by the short name,
 * because two people can share one ("o.petrenko" and "o.petrenko2" both become "O. Petrenko").
 *
 * Not every actor is a person: a card made by the lifecycle platform carries its key's name
 * (`Exchange_Data_Key_Kairos_Mornhouse_Product_Platform`). That is shown as words, not initials.
 */

export interface Shown {
  name: string
  initials: string
  /** 0..359, from the whole identity, so one person is one colour on everybody's screen */
  hue: number
  person: boolean
  /** Done by this person's agent, not by them (owner, 2026-10-06: "людина + Claude") — the service
   *  signs it `olya@mornhouse.com + Claude`; older rows say `olya@mornhouse.com (Claude)`. */
  via?: string
}

//: `email + Claude` (now) or `email (Claude)` (cards added over MCP before 2026-10-06).
const VIA = /\s*(?:\+\s*([A-Za-z][\w ]*?)|\(([A-Za-z][\w ]*?)\))\s*$/

function cap(part: string): string {
  return part ? part[0]!.toUpperCase() + part.slice(1) : part
}

export function personOf(who: string | null | undefined): Shown {
  const full = (who ?? '').trim()
  const m = full.includes('@') ? VIA.exec(full) : null
  if (m) {
    //: The person first, so one colour and one name per person whether they or their Claude acted.
    return { ...personOf(full.slice(0, m.index)), via: (m[1] ?? m[2])!.trim() }
  }
  const raw = full
  let hue = 0
  for (const ch of raw) hue = (hue * 31 + ch.codePointAt(0)!) % 360
  if (!raw.includes('@')) {
    const words = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
    return { name: words || '—', initials: (words[0] ?? '?').toUpperCase(), hue, person: false }
  }
  const local = raw.split('@')[0]!.replace(/\d+$/, '')
  const parts = local.split(/[._-]+/).filter(Boolean)
  const name = parts.map((p) => (p.length === 1 ? `${p.toUpperCase()}.` : cap(p))).join(' ') || raw
  const initials = (parts.length > 1 ? parts[0]![0]! + parts[parts.length - 1]![0]! : (parts[0] ?? '?').slice(0, 2))
    .toUpperCase()
  return { name, initials, hue, person: true }
}
